import { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Link2, MessageCircle, Plus, RefreshCw, ShieldOff, UserPlus } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { toast } from '@/stores/toastStore';
import {
  useIntervenants,
  useCreateIntervenant,
  useUpdateIntervenant,
  useGenerateAcces,
  useRevokeAcces,   // utilisé dans RevoquerDialog
  useSites,
} from '@/hooks/useTerrainAdmin';
import type { Intervenant, LienGenere } from '@/api/terrain-admin';
import { Badge, Modal, ModalHeader, type BadgeTone } from './_ui';

/**
 * SIMG-TERRAIN-2026-001 — Page "Intervenants & accès".
 *
 * Fusion des 2 concepts : chaque intervenant liste son statut d'accès actif,
 * avec CTA "Générer un lien" qui ouvre une modal affichant l'URL + QR + bouton
 * WhatsApp. Le secret n'apparaît qu'UNE FOIS — s'il est fermé sans être envoyé,
 * il faut régénérer.
 */
export const IntervenantsAccesPage = () => {
  const { data: intervenants = [], isLoading, refetch } = useIntervenants();
  const { data: sites = [] } = useSites();
  const [showCreate, setShowCreate]   = useState(false);
  const [lienGenere, setLienGenere]   = useState<LienGenere | null>(null);
  const [intervenantLien, setIntervenantLien] = useState<Intervenant | null>(null);
  const [showRevoke, setShowRevoke]   = useState<Intervenant | null>(null);

  const generate = useGenerateAcces();
  const update   = useUpdateIntervenant();

  const genererLien = (iv: Intervenant) => {
    generate.mutate(
      { intervenantId: iv.id, motif: `Généré depuis l'app siège pour ${iv.nom}` },
      { onSuccess: (data) => { setLienGenere(data); setIntervenantLien(iv); } },
    );
  };

  return (
    <>
      <Topbar
        title="Intervenants & accès terrain"
        subtitle="Créer, activer/désactiver un intervenant et gérer son lien personnel"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} title="Rafraîchir">
              <RefreshCw size={14} />
            </button>
            <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setShowCreate(true)}>
              <UserPlus size={14} /> Nouvel intervenant
            </button>
          </>
        }
      />
      <div className="ds-content">
        {isLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>}

        {!isLoading && intervenants.length === 0 && (
          <div className="ds-panel" style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ color: 'var(--tx-2)' }}>Aucun intervenant.</p>
            <button className="ds-btn ds-btn-p" style={{ marginTop: 12 }} onClick={() => setShowCreate(true)}>
              <UserPlus size={14} /> Créer le premier
            </button>
          </div>
        )}

        {intervenants.length > 0 && (
          <div className="ds-panel" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Fonction</th>
                  <th>Site</th>
                  <th>Téléphone</th>
                  <th>Accès</th>
                  <th title="Personne désignée pour émettre des bons de livraison signés depuis le portail terrain">Émet des BL</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {intervenants.map((iv) => (
                  <IntervenantRow
                    key={iv.id}
                    iv={iv}
                    onGenerer={() => genererLien(iv)}
                    onRevoquer={() => setShowRevoke(iv)}
                    onToggleActif={() => update.mutate({ id: iv.id, patch: { actif: !iv.actif } })}
                    onToggleBl={() => update.mutate({ id: iv.id, patch: { peutEmettreBl: !iv.peutEmettreBl } })}
                    generatingId={generate.isPending ? undefined : null}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateIntervenantDialog
          sites={sites}
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      )}

      {lienGenere && intervenantLien && (
        <LienGenereDialog
          lien={lienGenere}
          intervenant={intervenantLien}
          onClose={() => { setLienGenere(null); setIntervenantLien(null); }}
        />
      )}

      {showRevoke && (
        <RevoquerDialog
          intervenant={showRevoke}
          onClose={() => setShowRevoke(null)}
          onRevoked={() => setShowRevoke(null)}
        />
      )}
    </>
  );
};

// ─── Row ────────────────────────────────────────────────────────

function IntervenantRow({
  iv, onGenerer, onRevoquer, onToggleActif, onToggleBl,
}: {
  iv: Intervenant;
  onGenerer: () => void;
  onRevoquer: () => void;
  onToggleActif: () => void;
  onToggleBl: () => void;
  generatingId?: string | null;
}) {
  const badge = statutAcces(iv);
  return (
    <tr>
      <td style={{ fontWeight: 600 }}>{iv.nom}</td>
      <td style={{ color: 'var(--tx-2)' }}>{iv.fonction}</td>
      <td>{iv.site.code}</td>
      <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{iv.telephone}</td>
      <td><Badge tone={badge.tone}>{badge.label}</Badge></td>
      <td>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: iv.actif ? 'pointer' : 'not-allowed' }}>
          <input
            type="checkbox"
            checked={!!iv.peutEmettreBl}
            disabled={!iv.actif}
            onChange={onToggleBl}
            aria-label={`${iv.nom} peut émettre des bons de livraison`}
          />
          {iv.peutEmettreBl ? 'Oui' : 'Non'}
        </label>
      </td>
      <td style={{ textAlign: 'right' }}>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          {iv.actif && (
            <button className="ds-btn ds-btn-p ds-btn-sm" onClick={onGenerer} title="Générer un nouveau lien (l'ancien sera révoqué)">
              <Link2 size={13} /> Générer lien
            </button>
          )}
          {iv.accesActif && (
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={onRevoquer} title="Révoquer l'accès actif">
              <ShieldOff size={13} />
            </button>
          )}
          <button
            className="ds-btn ds-btn-g ds-btn-sm"
            onClick={onToggleActif}
            title={iv.actif ? 'Désactiver (révoque l\'accès en cascade)' : 'Réactiver'}
          >
            {iv.actif ? 'Désactiver' : 'Réactiver'}
          </button>
        </div>
      </td>
    </tr>
  );
}

function statutAcces(iv: Intervenant): { label: string; tone: BadgeTone } {
  if (!iv.actif) return { label: 'Intervenant désactivé', tone: 'neutral' };
  if (!iv.accesActif) return { label: 'Aucun lien', tone: 'warning' };
  const now = Date.now();
  const exp = new Date(iv.accesActif.expireLe).getTime();
  if (exp < now) return { label: 'Expiré', tone: 'danger' };
  if (iv.accesActif.dernierUsage) return { label: `Actif · usage ${dateShort(iv.accesActif.dernierUsage)}`, tone: 'success' };
  return { label: 'En attente 1er usage', tone: 'info' };
}

function dateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

// ─── Dialog : créer intervenant ─────────────────────────────────

function CreateIntervenantDialog({ sites, onClose, onCreated }: {
  sites: Array<{ id: string; code: string; libelle: string }>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [nom, setNom]             = useState('');
  const [fonction, setFonction]   = useState('chef_site');
  const [siteId, setSiteId]       = useState(sites[0]?.id ?? '');
  const [telephone, setTelephone] = useState('');
  const create = useCreateIntervenant();

  // Détail des champs manquants pour l'utilisateur (au lieu d'un simple bouton grisé)
  const champsManquants: string[] = [];
  if (nom.trim().length === 0)          champsManquants.push('Nom');
  if (!fonction)                        champsManquants.push('Fonction');
  if (!siteId)                          champsManquants.push('Site');
  if (telephone.trim().length <= 5)     champsManquants.push('Téléphone (6 chiffres min)');
  const canSubmit = champsManquants.length === 0;

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      { nom: nom.trim(), fonction, siteId, telephone: telephone.trim() },
      { onSuccess: onCreated },
    );
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 520, width: '100%', padding: 20, background: '#fff' }}>
        <ModalHeader titre="Nouvel intervenant" onClose={onClose} />
        <div style={{ display: 'grid', gap: 12 }}>
          <Champ label="Nom complet" value={nom} onChange={setNom} placeholder="ex. Kouassi Adama" />
          <Champ label="Fonction" value={fonction} onChange={setFonction} placeholder="ex. chef_site, cariste, responsable_production" />
          <ChampSelect label="Site" value={siteId} onChange={setSiteId} options={sites.map((s) => ({ value: s.id, label: `${s.code} — ${s.libelle}` }))} />
          <Champ label="Téléphone (format international WhatsApp)" value={telephone} onChange={setTelephone} placeholder="+2250700000000" />
        </div>
        {!canSubmit && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px' }}>
            À compléter : <strong>{champsManquants.join(', ')}</strong>
          </div>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={submit} disabled={!canSubmit || create.isPending}>
            {create.isPending ? 'Création…' : <><Plus size={14} /> Créer</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Dialog : lien généré (QR + copie + WhatsApp) ─────────────

function LienGenereDialog({ lien, intervenant, onClose }: {
  lien: LienGenere;
  intervenant: Intervenant;
  onClose: () => void;
}) {
  const numeroWhatsApp = useMemo(() => intervenant.telephone.replace(/[^0-9]/g, ''), [intervenant.telephone]);
  const messageWA = `Bonjour ${intervenant.nom}, voici votre lien personnel pour le Portail Terrain LEAD BETON. Ouvrez-le sur votre téléphone : ${lien.url}`;
  const lienWA = `https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(messageWA)}`;

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(lien.url);
      toast.success('Lien copié', 'Collez-le dans WhatsApp ou SMS');
    } catch {
      toast.error('Copie impossible', 'Copiez manuellement le lien affiché');
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 560, width: '100%', padding: 24 }}>
        <ModalHeader titre={`Lien pour ${intervenant.nom}`} onClose={onClose} />

        <div className="ds-alert-strip ds-alert-i" style={{ marginBottom: 16 }}>
          <div className="ds-alert-txt">
            <strong>Ce lien n'apparaîtra qu'une seule fois.</strong> Envoyez-le maintenant par WhatsApp.
            Le fermer sans l'envoyer oblige à en régénérer un nouveau.
          </div>
        </div>

        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'auto 1fr', alignItems: 'center' }}>
          <div style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1px solid var(--bd-2)' }}>
            <QRCodeSVG value={lien.url} size={168} level="M" />
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--tx-3)', marginBottom: 4 }}>URL</div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: 'var(--tx-1)' }}>{lien.url}</div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>
              Expire le <strong>{new Date(lien.expireLe).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a
            href={lienWA} target="_blank" rel="noreferrer"
            className="ds-btn ds-btn-p"
            style={{ flexGrow: 1, justifyContent: 'center' }}
          >
            <MessageCircle size={14} /> Envoyer sur WhatsApp
          </a>
          <button className="ds-btn ds-btn-g" onClick={copier}>
            <Copy size={14} /> Copier
          </button>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--tx-3)', textAlign: 'center' }}>
          Le lien verrouille le premier téléphone qui l'ouvre. Cet appareil devient le seul autorisé.
        </div>
      </div>
    </Modal>
  );
}

// ─── Dialog : révoquer ─────────────────────────────────────────

function RevoquerDialog({ intervenant, onClose, onRevoked }: {
  intervenant: Intervenant;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const [motif, setMotif] = useState('');
  const revoke = useRevokeAcces();

  const submit = () => {
    if (motif.trim().length < 5 || !intervenant.accesActif) return;
    revoke.mutate({ id: intervenant.accesActif.id, motif: motif.trim() }, { onSuccess: onRevoked });
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 460, width: '100%', padding: 20 }}>
        <ModalHeader titre={`Révoquer l'accès de ${intervenant.nom}`} onClose={onClose} />
        <p style={{ fontSize: 14, color: 'var(--tx-2)', marginBottom: 12 }}>
          L'intervenant ne pourra plus se connecter au portail terrain avec ce lien. Un nouveau lien pourra être généré à tout moment.
        </p>
        <Champ label="Motif (min 5 caractères) — tracé pour audit" value={motif} onChange={setMotif} placeholder="ex. Départ du personnel, téléphone perdu…" />
        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={submit} disabled={motif.trim().length < 5 || revoke.isPending}>
            {revoke.isPending ? 'Révocation…' : 'Confirmer la révocation'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Primitives champs (Modal + ModalHeader importés depuis ./_ui) ─────

function Champ({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>{label}</div>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{
          width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8,
          fontSize: 14, color: 'var(--tx-1)', background: 'var(--bg-1)',
        }}
      />
    </label>
  );
}

function ChampSelect({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>{label}</div>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8,
          fontSize: 14, color: 'var(--tx-1)', background: 'var(--bg-1)',
        }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
